<template>
  <!-- Add / edit one constat: date du constat, statut, type, description,
       photos (camera or upload) -->
  <b-modal
    :active="active"
    :width="640"
    :can-cancel="['escape', 'x', 'outside']"
    @close="$emit('close')"
  >
    <div class="modal-card constat-modal">
      <header class="modal-card-head">
        <p class="modal-card-title title is-6 mb-0">
          {{ element ? $t("edit_constat") : $t("add_constat") }}
        </p>
        <button
          type="button"
          class="delete"
          aria-label="close"
          @click="$emit('close')"
        />
      </header>

      <section class="modal-card-body">
        <div class="columns is-multiline is-variable is-3">
          <div class="column is-half">
            <b-field :label="$t('inspection_date_label')">
              <b-input
                v-model="form.date"
                type="date"
                required
              />
            </b-field>
          </div>
          <!-- Status is not picked here: Inspection = Complété, every
               other type = Action requise (changed later from the list) -->
          <div class="column is-half">
            <b-field>
              <template #label>
                {{ $t('type') }} <span class="has-text-danger">*</span>
              </template>
              <b-select
                v-model="form.type"
                expanded
                required
              >
                <option value="">
                  {{ $t("select").capitalize() }}
                </option>
                <option
                  v-for="option in typeOptions"
                  :key="option"
                  :value="option"
                >
                  {{ $t(option).capitalize() }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="column is-full">
            <b-field :label="$t('description').capitalize()">
              <b-input
                v-model="form.notes"
                type="textarea"
                rows="4"
              />
            </b-field>
          </div>

          <!-- Photos: only when creating (existing constats manage their
               photos from the Voir popup) -->
          <div
            v-if="!element"
            class="column is-full"
          >
            <label class="label">{{ $t("photos").capitalize() }}</label>
            <v-photo-picker
              :loading="submitting"
              @files="addFiles"
            />
            <div
              v-if="photos.length"
              class="constat-thumbs mt-3"
            >
              <div
                v-for="(photo, index) in photos"
                :key="index"
                class="constat-thumb"
              >
                <img :src="photo.preview">
                <button
                  type="button"
                  class="delete is-small"
                  :title="$t('delete').capitalize()"
                  @click="photos.splice(index, 1)"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer class="modal-card-foot is-justify-content-flex-end">
        <b-button
          :label="$t('cancel').capitalize()"
          @click="$emit('close')"
        />
        <b-button
          type="is-primary"
          :loading="submitting"
          :disabled="!form.type || !form.date"
          @click="submit"
        >
          {{ element ? $t("save") : $t("add") }}
        </b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import notificator from "@/services/notificator";
import PhotoPicker from "./PhotoPicker.vue";

export default {
  components: {
    "v-photo-picker": PhotoPicker,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    propertyId: {
      type: Number,
      default: null,
    },
    // Editing an existing constat (null = create)
    element: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      submitting: false,
      photos: [],
      form: this.emptyForm(),
    };
  },
  computed: {
    typeOptions() {
      return this.$laravel.propertyInspectionElementTypes || [];
    },
  },
  watch: {
    active(open) {
      if (open) {
        this.form = this.element ? this.formFrom(this.element) : this.emptyForm();
        this.photos = [];
      }
    },
  },
  methods: {
    emptyForm() {
      return {
        date: this.$dayjs().format("YYYY-MM-DD"),
        status: "STATUS_TO_DO",
        type: "",
        notes: "",
      };
    },
    formFrom(element) {
      return {
        date: element.date || this.$dayjs().format("YYYY-MM-DD"),
        status: element.status || "STATUS_TO_DO",
        type: element.type || "",
        notes: this.htmlToText(element.notes || ""),
      };
    },
    // Descriptions are stored as HTML (legacy rich text): one <p> per line
    textToHtml(text) {
      const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const lines = (text || "").split(/\r?\n/).map((l) => l.trim());

      return lines.filter((l, i) => l !== "" || i < lines.length - 1).map((l) => "<p>" + (escape(l) || "<br>") + "</p>").join("");
    },
    htmlToText(html) {
      return html
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>\s*<p[^>]*>/gi, "\n")
        .replace(/<\/(p|div|li)>/gi, "\n")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
        .trim();
    },
    addFiles(files) {
      files.forEach((file) => {
        this.photos.push({ file, preview: URL.createObjectURL(file) });
      });
    },
    submit() {
      if (this.submitting || !this.form.type || !this.form.date) {
        return;
      }
      this.submitting = true;

      const request = this.element ? this.update() : this.create();

      request
        .then((response) => {
          notificator.open({ message: response.data.message, type: "success" });
          this.$emit("saved");
          this.$emit("close");
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    create() {
      const data = new FormData();

      data.append("date", this.form.date);
      data.append("type", this.form.type);
      data.append("notes", this.textToHtml(this.form.notes));
      this.photos.forEach((photo) => data.append("photos[]", photo.file, photo.file.name));

      return this.$http.post(
        this.$laravel.route("api.property-inspection-elements.store", { property: this.propertyId }),
        data
      );
    },
    update() {
      return this.$http.patch(
        this.$laravel.route("api.property-inspection-elements.update", {
          propertyInspectionElement: this.element.id,
        }),
        { date: this.form.date, type: this.form.type, notes: this.textToHtml(this.form.notes) }
      );
    },
  },
};
</script>

<style scoped>
.constat-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.constat-thumb {
  position: relative;
  width: 84px;
  height: 84px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #dbdbdb;
}
.constat-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.constat-thumb .delete {
  position: absolute;
  top: 4px;
  right: 4px;
}
</style>
